import { formatDisplayName } from '../shared/displayName.js';

/**
 * Essentials overlay: ordered per-argument editors (custom Form / schema object / primitive).
 *
 * Custom forms always receive FormModel = the full Arguments bag. The UserControl decides
 * which argument(s) to edit (flat keys, or Model bound to FormModel.Settings, etc.).
 * resolveLegacyToolFormArgName only picks which schema-object slot tool.Form replaces so
 * we do not also render a SchemaForm for that object — not for FormModel binding.
 */

/** Same skips as FlowV2 generateSchemaFromArgs. Pass mediaType to skip the channel media arg. */
export function isSkippedPaletteArgument(a, mediaType) {
    if (!a)
        return true;
    if (a.Hidden === true || a.Hidden === 'true')
        return true;
    const argType = a.ArgType ?? a.Type;
    if (mediaType) {
        if (argType == mediaType)
            return true;
    }
    else if (argType == 'Platform.VoiceExt.v3') {
        // Legacy: no mediaType passed → still hide voice media arg
        return true;
    }
    return a.DefaultValue == '{$Current}'
        || a.Out
        || a.Direction == 1;
}

/** Null, blank, and void method signatures produce no return value. */
export function isVoidMethodReturn(returnType) {
    if (returnType == null)
        return true;
    const text = String(returnType).trim();
    return text === '' || text.toLowerCase() === 'void';
}

export function isPlatformSchemaArg(a) {
    const argType = a.ArgType ?? a.Type ?? '';
    return typeof argType === 'string' && argType.startsWith('Platform.Schema.');
}

/**
 * Ensure args[name] is a mutable plain object (for nested schema SchemaForm models).
 * @param {object} args
 * @param {string} name
 */
export function ensureArgumentObject(args, name) {
    if (!args || typeof args !== 'object')
        return {};
    const cur = args[name];
    if (cur == null || typeof cur !== 'object' || Array.isArray(cur))
        args[name] = {};
    return args[name];
}

/**
 * True when Essentials should use the interleaved stack instead of buildEssentialIconForm.
 * @param {object} tool
 */
export function shouldUseMixedEssentialStack(tool) {
    if (!tool)
        return false;
    if (tool.Form)
        return true;
    const toolArgs = Array.isArray(tool.Arguments) ? tool.Arguments : [];
    return toolArgs.some(a => a && a.Form);
}

/**
 * Resolve which schema arg receives legacy tool.Form when no arg.Form exists.
 * Prefer Name === 'Settings', else first Platform.Schema.* arg.
 * Used only to skip a duplicate schemaObject editor — FormModel is always the full bag.
 * @param {object} tool
 * @returns {string|null}
 */
export function resolveLegacyToolFormArgName(tool) {
    const toolArgs = Array.isArray(tool?.Arguments) ? tool.Arguments : [];
    const schemaArgs = toolArgs.filter(a => !isSkippedPaletteArgument(a) && isPlatformSchemaArg(a));
    if (!schemaArgs.length)
        return null;
    const settings = schemaArgs.find(a => a.Name === 'Settings');
    return (settings || schemaArgs[0]).Name;
}

const FALLBACK_CAPTION_PROP = { type: 'string', title: 'Caption' };
/** Wrapper schema reuse so SchemaForm does not remount Caption on every overlay render. */
const captionSchemaCache = new WeakMap();

/**
 * Caption field editor for the top of the Essentials mixed stack (binds model.API.Caption).
 * @param {object} model  icon model { API, Arguments, ... }
 * @param {object} rootSchema  full icon schema
 * @returns {{ kind: string, argName: string, schema: object, form: string[], cmodel: object }|null}
 */
export function getCaptionEditorDescriptor(model, rootSchema) {
    if (!model?.API || typeof model.API !== 'object')
        return null;
    const captionProp = rootSchema?.properties?.API?.properties?.Caption;
    const prop = captionProp && typeof captionProp === 'object'
        ? captionProp
        : FALLBACK_CAPTION_PROP;
    let schema = captionSchemaCache.get(prop);
    if (!schema) {
        schema = {
            type: 'object',
            properties: { Caption: prop },
        };
        captionSchemaCache.set(prop, schema);
    }
    return {
        kind: 'caption',
        argName: 'Caption',
        schema,
        form: ['Caption'],
        cmodel: model.API,
    };
}

/**
 * Output / ReturnVar editor for the Essentials mixed stack (binds model.Output).
 * Uses AllowVariableTarget leaves from generateSchemaFromArgs.
 * @param {object} model  icon model { API, Arguments, Output, ... }
 * @param {object} rootSchema  full icon schema
 * @returns {{ kind: string, argName: string, schema: object, form: string[], cmodel: object }|null}
 */
export function getOutputEditorDescriptor(model, rootSchema) {
    const outputSchema = rootSchema?.properties?.Output;
    if (!outputSchema || typeof outputSchema !== 'object')
        return null;
    const props = outputSchema.properties;
    if (!props || typeof props !== 'object' || !Object.keys(props).length)
        return null;
    if (!model || typeof model !== 'object')
        return null;
    if (model.Output == null || typeof model.Output !== 'object' || Array.isArray(model.Output))
        model.Output = {};
    return {
        kind: 'output',
        argName: 'Output',
        schema: {
            type: 'object',
            properties: { ...props },
        },
        form: ['*'],
        cmodel: model.Output,
    };
}

/**
 * Ordered Essentials editors for a tool.
 * @param {object} tool
 * @param {object} argumentsModel  live model.Arguments
 * @param {object} argumentsSchemaProperties  schema.properties.Arguments.properties
 * @returns {Array<{ kind: string, argName: string, formUrl?: string, schema?: object, form?: Array, cmodel: object }>}
 */
export function listEssentialArgumentEditors(tool, argumentsModel, argumentsSchemaProperties) {
    const args = argumentsModel && typeof argumentsModel === 'object' ? argumentsModel : {};
    const props = argumentsSchemaProperties || {};
    const toolArgs = Array.isArray(tool?.Arguments) ? tool.Arguments : [];

    const anyArgForm = toolArgs.some(a => a && a.Form);
    const legacyForm = !anyArgForm && tool?.Form ? tool.Form : null;
    const legacyTargetName = legacyForm ? resolveLegacyToolFormArgName(tool) : null;

    const editors = [];

    // tool.Form with no Platform.Schema.* target: one full-bag custom form owns all inbound args.
    if (legacyForm && !legacyTargetName) {
        editors.push({
            kind: 'customForm',
            argName: '_Arguments',
            formUrl: legacyForm,
            cmodel: args,
        });
        return editors;
    }

    for (const a of toolArgs) {
        if (isSkippedPaletteArgument(a))
            continue;

        const name = a.Name;
        const propSchema = props[name];
        const useLegacyForm = legacyForm && legacyTargetName && name === legacyTargetName;

        if ((a.Form) || useLegacyForm) {
            // Ensure nested schema objects exist for forms that bind Model to FormModel.<Arg>.
            if (isPlatformSchemaArg(a))
                ensureArgumentObject(args, name);
            // FormModel is always the full Arguments bag; the UserControl picks fields.
            editors.push({
                kind: 'customForm',
                argName: name,
                formUrl: a.Form || legacyForm,
                cmodel: args,
            });
            continue;
        }

        if (isPlatformSchemaArg(a)) {
            editors.push({
                kind: 'schemaObject',
                argName: name,
                schema: propSchema && typeof propSchema === 'object'
                    ? propSchema
                    : { type: 'object', properties: {} },
                form: ['*'],
                cmodel: ensureArgumentObject(args, name),
            });
            continue;
        }

        const fieldSchema = propSchema && typeof propSchema === 'object'
            ? propSchema
            : { type: 'string', title: formatDisplayName(name) };
        editors.push({
            kind: 'primitive',
            argName: name,
            schema: {
                type: 'object',
                properties: { [name]: fieldSchema },
            },
            form: [name],
            cmodel: args,
        });
    }

    return editors;
}
